import { useQueryClient } from "@tanstack/react-query"
import * as React from "react"

import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import {
  AuthorSide,
  type Authority,
  type Evidence,
  type Finding,
  type FindingAxes,
  type FindingGroup,
  type FindingId,
  type FindingSeverity,
  type Message,
  type MessageId,
  type PointRect,
  type Review,
  type ReviewId,
  type ReviewStage,
  type Role,
  type UserId,
} from "../data/review-data"

import { reviewCache } from "./review-cache"
import { useMutation, type MutationResult } from "./use-mutation"

/**
 * Review mutations. Each hook follows the same three-step lifecycle:
 *
 *   1. `onMutate` snapshots the current cache entry and applies the
 *      optimistic patch.
 *   2. `mutationFn` performs the network call under an AbortSignal.
 *   3. `onError` restores the snapshot so the UI reflects the server's
 *      view even after a failure or a client-side abort.
 *
 * All state mutations flow through `reviewCache`; direct writes to
 * `Review` values are forbidden. This is what keeps optimistic updates,
 * WebSocket-driven invalidations, and rollback consistent.
 */

const MUTATION_LATENCY_MS = 220

function serverDelay(signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, MUTATION_LATENCY_MS)
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer)
        reject(new DOMException("Aborted", "AbortError"))
      },
      { once: true },
    )
  })
}

function localId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

interface Ctx {
  previous: Review | null
}

function patchReview(
  reviewId: ReviewId,
  patch: (prev: Review) => Review,
): { previous: Review | null } {
  const previous = reviewCache.get(reviewId) ?? null
  if (previous) reviewCache.set(reviewId, patch(previous))
  return { previous }
}

function rollback(reviewId: ReviewId, previous: Review | null): void {
  if (previous) reviewCache.set(reviewId, previous)
}

/* ── Verdict ───────────────────────────────────────────────────────────── */

export interface SetVerdictVars {
  reviewId: ReviewId
  findingId: FindingId
  verdict: FindingAxes["verdict"]
}

/**
 * Verdict toggle. The exposed `undo` reverses the previous mutation's
 * effect by writing the prior verdict back; this is what "Undo" in the
 * toast bar reaches for.
 *
 * The mutation itself follows the toggle semantic from the spec:
 * repeating a verdict clears it to `unset`. Callers therefore should
 * not implement their own toggle logic on top of `mutate`.
 */
export function useSetVerdictMutation(): MutationResult<SetVerdictVars> & {
  undo: (vars: { reviewId: ReviewId; findingId: FindingId }) => void
} {
  const lastPrevRef = React.useRef<Map<string, FindingAxes["verdict"]>>(new Map())

  const mutation = useMutation<SetVerdictVars, Ctx>({
    mutationFn: async (_vars, signal) => {
      await serverDelay(signal)
    },
    onMutate: ({ reviewId, findingId, verdict }) => {
      const { previous } = patchReview(reviewId, (prev) => ({
        ...prev,
        findings: prev.findings.map((f) => {
          if (f.id !== findingId) return f
          lastPrevRef.current.set(`${reviewId}:${findingId}`, f.axes.verdict)
          const next = f.axes.verdict === verdict ? "unset" : verdict
          return { ...f, axes: { ...f.axes, verdict: next } }
        }),
      }))
      return { previous }
    },
    onError: (_err, vars, ctx) => {
      rollback(vars.reviewId, ctx?.previous ?? null)
    },
  })

  const undo = React.useCallback(
    ({ reviewId, findingId }: { reviewId: ReviewId; findingId: FindingId }) => {
      const key = `${reviewId}:${findingId}`
      const prev = lastPrevRef.current.get(key)
      if (prev === undefined) return
      lastPrevRef.current.delete(key)
      patchReview(reviewId, (r) => ({
        ...r,
        findings: r.findings.map((f) =>
          f.id === findingId ? { ...f, axes: { ...f.axes, verdict: prev } } : f,
        ),
      }))
    },
    [],
  )

  return { ...mutation, undo }
}

/* ── Mark corrected ────────────────────────────────────────────────────── */

export interface MarkCorrectedVars {
  reviewId: ReviewId
  findingId: FindingId
}

/**
 * Confirms a proposed correction. The state axis only permits the
 * transition `candidate_corrected → corrected_confirmed`; the mutation
 * is a no-op for findings in any other state. Do not use this hook to
 * force earlier transitions — the pipeline owns them.
 */
export function useMarkCorrectedMutation(): MutationResult<MarkCorrectedVars> {
  return useMutation<MarkCorrectedVars, Ctx>({
    mutationFn: async (_vars, signal) => {
      await serverDelay(signal)
    },
    onMutate: ({ reviewId, findingId }) => {
      const { previous } = patchReview(reviewId, (prev) => ({
        ...prev,
        findings: prev.findings.map((f) => {
          if (f.id !== findingId) return f
          if (f.axes.state !== "candidate_corrected") return f
          return { ...f, axes: { ...f.axes, state: "corrected_confirmed" } }
        }),
      }))
      return { previous }
    },
    onError: (_e, vars, ctx) => rollback(vars.reviewId, ctx?.previous ?? null),
  })
}

/* ── Edit narrative ────────────────────────────────────────────────────── */

export interface EditNarrativeVars {
  reviewId: ReviewId
  findingId: FindingId
  patch: {
    claim?: string
    reasoning?: string | null
    remediation?: string | null
    divergence?: Evidence | null
    authorities?: Authority[]
  }
}

export function useEditNarrativeMutation(): MutationResult<EditNarrativeVars> {
  return useMutation<EditNarrativeVars, Ctx>({
    mutationFn: async (_vars, signal) => {
      await serverDelay(signal)
    },
    onMutate: ({ reviewId, findingId, patch }) => {
      const { previous } = patchReview(reviewId, (prev) => ({
        ...prev,
        findings: prev.findings.map((f) => {
          if (f.id !== findingId) return f
          return {
            ...f,
            claim: patch.claim ?? f.claim,
            reasoning: patch.reasoning === undefined ? f.reasoning : patch.reasoning,
            remediation:
              patch.remediation === undefined ? f.remediation : patch.remediation,
            divergence:
              patch.divergence === undefined ? f.divergence : patch.divergence,
            authorities: patch.authorities ?? f.authorities,
          }
        }),
      }))
      return { previous }
    },
    onError: (_e, vars, ctx) => rollback(vars.reviewId, ctx?.previous ?? null),
  })
}

/* ── Add finding ───────────────────────────────────────────────────────── */

const GROUP_LOOKUP: Record<"g-text" | "g-format", FindingGroup> = {
  "g-text": { id: "g-text", label: "Text review", ordinal: 3 },
  "g-format": { id: "g-format", label: "Formatting review", ordinal: 4 },
}

/**
 * Reviewer-authored finding. Mirrors `ManualFindingCreate` in
 * pyxis/api/src/pyxisrc_ai/schemas/finding_io.py with two shape
 * adjustments the wire mapping absorbs:
 *
 *   • `severity` on the wire is `"error" | "warn"`; the FE keeps the
 *     richer `"error" | "warning" | "info"` and maps at the seam
 *     (`warning → warn`, `info → error`) until the backend widens.
 *   • `divergence` on the wire is a plain string; the FE keeps the
 *     structured `{master, printed}` because the read views need both
 *     halves. The wire mapper serializes as `Master: X / FPL: Y`.
 *
 * `occurrences` is derived at the seam from `bbox` + `pageHint`: one
 * occurrence with the drawn box when we have one, empty otherwise.
 */
export interface AddFindingVars {
  reviewId: ReviewId
  input: {
    ruleId: string
    severity: FindingSeverity
    claim: string
    reasoning: string | null
    remediation: string | null
    authorities: Authority[]
    bbox: PointRect | null
    pageHint: number | null
    groupId: "g-text" | "g-format"
    /** Named page region resolved from the drawn box, if any. */
    sectionId: string | null
    divergence: Evidence | null
  }
}

export function useAddFindingMutation(): MutationResult<AddFindingVars> & {
  lastCreatedId: FindingId | null
} {
  const [lastCreatedId, setLastCreatedId] = React.useState<FindingId | null>(null)

  const mutation = useMutation<AddFindingVars, Ctx>({
    mutationFn: async (_vars, signal) => {
      await serverDelay(signal)
    },
    onMutate: ({ reviewId, input }) => {
      const now = new Date().toISOString()
      const id = `f-${localId()}` as FindingId
      setLastCreatedId(id)
      const { previous } = patchReview(reviewId, (prev) => {
        const nextNumber = prev.findings.reduce((m, f) => Math.max(m, f.number), 0) + 1
        const finding: Finding = {
          id,
          number: nextNumber,
          ruleId: input.ruleId,
          severity: input.severity,
          claim: input.claim,
          placement: input.bbox ? "box" : "document",
          bbox: input.bbox,
          pageHint: input.pageHint,
          occurrences: 1,
          axes: {
            verdict: "unset",
            state: "open",
            clientResolution: "none",
            origin: "manual",
          },
          confidence: null,
          authoredBy: { name: "You", at: now },
          group: GROUP_LOOKUP[input.groupId],
          kind: "judgment",
          divergence: input.divergence,
          reasoning: input.reasoning,
          remediation: input.remediation,
          authorities: input.authorities,
          firstSeenVersion: prev.version,
          commentCount: 0,
          unreadCount: 0,
          smeNote: null,
          dismissalNote: null,
        }
        return { ...prev, findings: [...prev.findings, finding] }
      })
      return { previous }
    },
    onError: (_e, vars, ctx) => rollback(vars.reviewId, ctx?.previous ?? null),
  })

  return { ...mutation, lastCreatedId }
}

/* ── Assignment ──────────────────────────────────────────────────────────
 * These mirror the backend workflow endpoints one-for-one:
 *   POST /reviews/:id/assign         — assign or reassign
 *   POST /reviews/:id/throwback      — return to pool
 *   POST /reviews/:id/advance-stage  — initial → secondary → done
 *   POST /reviews/:id/archive        — archive; last stage is preserved
 *
 * Each mutation appends one AssignmentEvent to the trail. Actions are
 * distinct: an "assign" over an empty assignee reads differently than a
 * "reassign" that displaces someone. The trail records both.
 */

export interface AssignVars {
  reviewId: ReviewId
  assigneeId: UserId
  assigneeName: string
  assigneeRole: Role
  note: string | null
}

export function useAssignReviewMutation(): MutationResult<AssignVars> {
  const qc = useQueryClient()
  return useMutation<AssignVars, Ctx>({
    mutationFn: async ({ reviewId, assigneeId, note }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/assign`,
        { user_id: Number(assigneeId), note },
        { signal },
      )
    },
    onSuccess: ({ reviewId }) => {
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onMutate: ({ reviewId, assigneeId, assigneeName, assigneeRole, note }) => {
      const now = new Date().toISOString()
      const { previous } = patchReview(reviewId, (prev) => {
        const wasAssigned = prev.assignment.assignee !== null
        const fromUser = prev.assignment.assignee?.name ?? null
        return {
          ...prev,
          assignment: {
            ...prev.assignment,
            assignee: {
              userId: assigneeId,
              name: assigneeName,
              role: assigneeRole,
              sinceISO: now,
            },
            events: [
              ...prev.assignment.events,
              {
                action: wasAssigned ? "reassign" : "assign",
                at: now,
                actor: "You",
                fromUser,
                toUser: assigneeName,
                toStage: null,
                note,
              },
            ],
          },
        }
      })
      return { previous }
    },
    onError: (_e, vars, ctx) => rollback(vars.reviewId, ctx?.previous ?? null),
  })
}

interface StageVars {
  reviewId: ReviewId
}

const NEXT_STAGE: Record<ReviewStage, ReviewStage | null> = {
  initial: "secondary",
  secondary: "done",
  done: null,
}

function useStageMutation(
  mutationFn: (vars: StageVars, signal: AbortSignal) => Promise<void>,
  optimistic: (prev: Review, now: string) => Review,
): MutationResult<StageVars> {
  const qc = useQueryClient()
  return useMutation<StageVars, Ctx>({
    mutationFn,
    onMutate: ({ reviewId }) => {
      const now = new Date().toISOString()
      const { previous } = patchReview(reviewId, (prev) => optimistic(prev, now))
      return { previous }
    },
    onSuccess: ({ reviewId }) => {
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onError: (_e, vars, ctx) => rollback(vars.reviewId, ctx?.previous ?? null),
  })
}

export function useReturnToPoolMutation(): MutationResult<StageVars> {
  return useStageMutation(
    async ({ reviewId }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/throwback`,
        { note: null },
        { signal },
      )
    },
    (prev, now) => ({
      ...prev,
      assignment: {
        ...prev.assignment,
        assignee: null,
        events: [
          ...prev.assignment.events,
          {
            action: "throwback",
            at: now,
            actor: "You",
            fromUser: prev.assignment.assignee?.name ?? null,
            toUser: null,
            toStage: null,
            note: null,
          },
        ],
      },
    }),
  )
}

export function useAdvanceStageMutation(): MutationResult<StageVars> {
  return useStageMutation(
    async ({ reviewId }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/advance-stage`,
        { skip_secondary: false, note: null },
        { signal },
      )
    },
    (prev, now) => {
      const next = NEXT_STAGE[prev.assignment.currentStage]
      if (!next) return prev
      return {
        ...prev,
        assignment: {
          ...prev.assignment,
          currentStage: next,
          events: [
            ...prev.assignment.events,
            {
              action: "advance_stage",
              at: now,
              actor: prev.assignment.assignee?.name ?? "You",
              fromUser: null,
              toUser: null,
              toStage: next,
              note: null,
            },
          ],
        },
      }
    },
  )
}

export function useSkipSecondPassMutation(): MutationResult<StageVars> {
  return useStageMutation(
    async ({ reviewId }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/advance-stage`,
        { skip_secondary: true, note: null },
        { signal },
      )
    },
    (prev, now) => ({
      ...prev,
      assignment: {
        ...prev.assignment,
        currentStage: "done",
        secondarySkipped: true,
        events: [
          ...prev.assignment.events,
          {
            action: "skip_secondary",
            at: now,
            actor: prev.assignment.assignee?.name ?? "You",
            fromUser: null,
            toUser: null,
            toStage: "done",
            note: null,
          },
        ],
      },
    }),
  )
}

export function useArchiveReviewMutation(): MutationResult<StageVars> {
  return useStageMutation(
    async ({ reviewId }, signal) => {
      await http.post(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/archive`,
        { note: null },
        { signal },
      )
    },
    (prev, now) => ({
      ...prev,
      assignment: {
        ...prev.assignment,
        archived: true,
        events: [
          ...prev.assignment.events,
          {
            action: "archive",
            at: now,
            actor: prev.assignment.assignee?.name ?? "You",
            fromUser: null,
            toUser: null,
            toStage: null,
            note: null,
          },
        ],
      },
    }),
  )
}

/* ── Messages ──────────────────────────────────────────────────────────── */

export interface PostMessageVars {
  reviewId: ReviewId
  body: string
  findingId: FindingId | null
}

export function usePostMessageMutation(): MutationResult<PostMessageVars> {
  return useMutation<PostMessageVars, Ctx>({
    mutationFn: async (_v, signal) => {
      await serverDelay(signal)
    },
    onMutate: ({ reviewId, body, findingId }) => {
      const id = `m-${localId()}` as MessageId
      const message: Message = {
        id,
        authorName: "You",
        authorSide: AuthorSide.REVIEWER,
        body,
        at: new Date().toISOString(),
        findingId,
        mine: true,
      }
      const { previous } = patchReview(reviewId, (prev) => {
        const findings = findingId
          ? prev.findings.map((f) =>
              f.id === findingId ? { ...f, commentCount: f.commentCount + 1 } : f,
            )
          : prev.findings
        return { ...prev, findings, messages: [...prev.messages, message] }
      })
      return { previous }
    },
    onError: (_e, vars, ctx) => rollback(vars.reviewId, ctx?.previous ?? null),
  })
}

export interface MarkReadVars {
  reviewId: ReviewId
  findingId: FindingId | null
}

export function useMarkMessagesReadMutation(): MutationResult<MarkReadVars> {
  return useMutation<MarkReadVars, Ctx>({
    mutationFn: async (_v, signal) => {
      await serverDelay(signal)
    },
    onMutate: ({ reviewId, findingId }) => {
      if (!findingId) return { previous: null }
      const { previous } = patchReview(reviewId, (prev) => ({
        ...prev,
        findings: prev.findings.map((f) =>
          f.id === findingId ? { ...f, unreadCount: 0 } : f,
        ),
      }))
      return { previous }
    },
    onError: (_e, vars, ctx) => rollback(vars.reviewId, ctx?.previous ?? null),
  })
}
